import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Megaphone, CheckCircle2, XCircle, EyeOff, Star, Trash2, Plus, Globe, Settings2 } from 'lucide-react';
import { fmtDate } from '../lib/share';

/**
 * Panel Super Master: Marketplace + Promociones + Feature flags SaaS.
 */
export default function MarketplaceAdmin() {
  const [features, setFeatures] = useState(null);
  const [promos, setPromos] = useState([]);
  const [status, setStatus] = useState('pending'); // tab de pendings por defecto
  const [newP, setNewP] = useState({ title: '', description: '', image_url: '', category: '', cta_url: '', cta_label: 'Ver oferta', valid_from: '', valid_to: '' });

  const loadFeatures = () => api.getSaasFeatures().then((r) => setFeatures(r.data)).catch(() => {});
  const loadPromos = () => api.listPromotions(status ? { status } : {}).then((r) => setPromos(r.data || [])).catch(() => setPromos([]));
  useEffect(() => { loadFeatures(); }, []);
  useEffect(() => { loadPromos(); /* eslint-disable-next-line */ }, [status]);

  const toggleFeature = async (key, val) => {
    try { const r = await api.setSaasFeatures({ [key]: val }); setFeatures(r.data); toast.success(`SalesPRO: ${key} ${val ? 'activado' : 'desactivado'}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const createPromo = async () => {
    if (!newP.title.trim()) { toast.error('SalesPRO: Falta título'); return; }
    try {
      await api.createPromotion({ ...newP, valid_from: newP.valid_from || null, valid_to: newP.valid_to || null });
      toast.success('SalesPRO: Promoción publicada'); setNewP({ title: '', description: '', image_url: '', category: '', cta_url: '', cta_label: 'Ver oferta', valid_from: '', valid_to: '' }); loadPromos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const approve = async (p) => { try { await api.approvePromotion(p.promotion_id); toast.success('Aprobada y publicada'); loadPromos(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };
  const reject = async (p) => { const r = window.prompt('Razón de rechazo:', 'No cumple con las políticas'); if (r === null) return; try { await api.rejectPromotion(p.promotion_id, r); toast.success('Rechazada'); loadPromos(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };
  const hide = async (p) => { if (!window.confirm('¿Ocultar esta promoción del marketplace público?')) return; try { await api.hidePromotion(p.promotion_id); toast.success('Oculta'); loadPromos(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };
  const toggleFeatured = async (p) => { try { await api.featurePromotion(p.promotion_id, !p.featured); toast.success(p.featured ? 'Dejó de ser destacada' : '⭐ Destacada'); loadPromos(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };
  const remove = async (p) => { if (!window.confirm('¿Eliminar permanentemente?')) return; try { await api.deletePromotion(p.promotion_id); toast.success('Eliminada'); loadPromos(); } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } };

  const STATUS_LABEL = { published: '🟢 Publicada', pending: '🟡 Pendiente de revisión', draft: '📝 Borrador', rejected: '🔴 Rechazada', hidden: '👁️ Oculta' };

  return (
    <Card data-testid="marketplace-admin">
      <CardContent className="p-5 space-y-5">
        <div>
          <h3 className="font-heading font-bold text-[#022C22] flex items-center gap-2"><Megaphone size={18} /> Marketplace & Publicidad</h3>
          <p className="text-xs text-[#64748B]">Controla las ofertas que ven todos los clientes del ecosistema SalesPRO.</p>
        </div>

        {/* Feature flags */}
        {features && (
          <Card className="border-primary/40">
            <CardContent className="p-4 space-y-2">
              <p className="text-sm font-bold text-[#022C22] flex items-center gap-2"><Settings2 size={14} /> Interruptores globales (solo tú)</p>
              <div className="grid sm:grid-cols-2 gap-2 text-xs">
                {[
                  ['marketplace_enabled', 'Marketplace público activo', 'Si está apagado, nadie ve /marketplace'],
                  ['cross_tenant_clients_enabled', 'Enlazar cliente entre tenants', 'Un cliente puede pertenecer a varias empresas'],
                  ['email_notify_new_affiliation', 'Notificar afiliación por correo', 'Enviar email cuando otra empresa afilie a un cliente ya existente'],
                  ['tenant_promotion_proposals_enabled', 'Empresas pueden proponer promos', 'Si está apagado, solo tú y Right Hand crean promociones'],
                  ['master_approval_required', 'Promos de empresas requieren tu aprobación', 'Si está apagado, se publican directo (riesgoso)'],
                  ['prospect_signups_enabled', 'Permitir registro de prospectos', 'Usuarios sin empresa pueden crear cuenta desde /marketplace'],
                  ['affiliation_requests_enabled', 'Solicitudes de afiliación a empresas', 'Los prospectos pueden pedir afiliarse a una empresa'],
                ].map(([key, label, hint]) => (
                  <label key={key} className="flex items-start gap-2 p-2 bg-[#F8FAFC] rounded">
                    <Switch checked={!!features[key]} onCheckedChange={(v) => toggleFeature(key, v)} data-testid={`feat-${key}`} />
                    <div><b>{label}</b><p className="text-[10px] text-[#94A3B8]">{hint}</p></div>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Crear promoción directa (Master) */}
        <details className="border rounded-lg p-3">
          <summary className="text-sm font-semibold text-[#022C22] cursor-pointer flex items-center gap-1"><Plus size={14} /> Nueva promoción (publicación directa)</summary>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            <div><Label className="text-xs">Título *</Label><Input value={newP.title} onChange={(e) => setNewP({ ...newP, title: e.target.value })} data-testid="newpromo-title" /></div>
            <div><Label className="text-xs">Categoría</Label><Input value={newP.category} onChange={(e) => setNewP({ ...newP, category: e.target.value })} placeholder="celulares, ropa, accesorios…" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Descripción</Label><Input value={newP.description} onChange={(e) => setNewP({ ...newP, description: e.target.value })} /></div>
            <div><Label className="text-xs">URL imagen</Label><Input value={newP.image_url} onChange={(e) => setNewP({ ...newP, image_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label className="text-xs">URL del CTA</Label><Input value={newP.cta_url} onChange={(e) => setNewP({ ...newP, cta_url: e.target.value })} placeholder="https://celularbeach.com/iphone15" /></div>
            <div><Label className="text-xs">Vigencia desde</Label><Input type="date" value={newP.valid_from} onChange={(e) => setNewP({ ...newP, valid_from: e.target.value })} /></div>
            <div><Label className="text-xs">Vigencia hasta</Label><Input type="date" value={newP.valid_to} onChange={(e) => setNewP({ ...newP, valid_to: e.target.value })} /></div>
          </div>
          <Button onClick={createPromo} className="mt-3" data-testid="newpromo-create"><Plus size={14} className="mr-1" /> Publicar</Button>
        </details>

        {/* Lista */}
        <div>
          <div className="flex flex-wrap gap-1 mb-2">
            {['pending', 'published', 'rejected', 'hidden', ''].map((st) => (
              <button key={st} onClick={() => setStatus(st)} className={`text-[11px] px-2 py-1 rounded border ${status === st ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white border-[#E2E8F0] text-[#475569]'}`} data-testid={`promo-filter-${st || 'all'}`}>
                {st ? STATUS_LABEL[st] : 'Todas'}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {promos.length === 0 && <p className="text-xs text-[#94A3B8]">Sin promociones en este filtro.</p>}
            {promos.map((p) => (
              <div key={p.promotion_id} className="border rounded-lg p-3 bg-white" data-testid={`promo-item-${p.promotion_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-bold text-sm text-[#022C22] flex items-center gap-1">
                      {p.featured && <Star size={12} className="text-amber-500 fill-amber-500" />}
                      {p.title}
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B]">{STATUS_LABEL[p.status] || p.status}</span>
                    </p>
                    <p className="text-xs text-[#64748B]">{p.description}</p>
                    <p className="text-[10px] text-[#94A3B8] mt-1">
                      {p.tenant_name || <i>Sin tenant (creada por master)</i>} · {p.category || 'sin categoría'} · {fmtDate(p.created_at)}
                      {p.valid_to && ` · hasta ${p.valid_to}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {p.status === 'pending' && <Button size="sm" onClick={() => approve(p)} data-testid={`approve-${p.promotion_id}`}><CheckCircle2 size={12} className="mr-1" /> Aprobar</Button>}
                    {p.status === 'pending' && <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => reject(p)} data-testid={`reject-${p.promotion_id}`}><XCircle size={12} /></Button>}
                    {p.status === 'published' && <Button size="sm" variant="outline" onClick={() => toggleFeatured(p)} data-testid={`feat-${p.promotion_id}`}><Star size={12} /></Button>}
                    {p.status === 'published' && <Button size="sm" variant="outline" onClick={() => hide(p)} data-testid={`hide-${p.promotion_id}`}><EyeOff size={12} /></Button>}
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => remove(p)}><Trash2 size={12} /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-3 border-t text-center">
          <a href="/marketplace" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1"><Globe size={12} /> Ver marketplace público</a>
        </div>
      </CardContent>
    </Card>
  );
}
